<div class="subscription-profile">
    <div class="card">
        <div class="card__header">
            <div class="card__title">{{"subscription_profile" | translate}}</div>
        </div>
        <div class="card__body">
            <!-- Loading State -->
            <div *ngIf="isLoading" class="loading-state">
                <div class="spinner"></div>
                <p>{{"loading" | translate}}...</p>
            </div>

            <!-- No Subscription -->
            <div *ngIf="!isLoading && !subscription" class="no-subscription">
                <div class="no-subscription__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                </div>
                <h3>{{"no_active_subscription" | translate}}</h3>
                <p>{{"no_subscription_message" | translate}}</p>
            </div>

            <!-- Subscription Details -->
            <div *ngIf="!isLoading && subscription" class="subscription-details">
                <!-- Plan Info Section -->
                <div class="section">
                    <h3 class="section__title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                            <line x1="1" y1="10" x2="23" y2="10"></line>
                        </svg>
                        {{"plan_information" | translate}}
                    </h3>
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="info-label">{{"plan_name" | translate}}</span>
                            <span class="info-value">{{ getPlanName() }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"status" | translate}}</span>
                            <span class="info-value">
                                <span class="status-badge" [ngClass]="getStatusClass(subscription.status)">
                                    {{ subscription.status | titlecase }}
                                </span>
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"billing_type" | translate}}</span>
                            <span class="info-value">{{ subscription.billing_type | titlecase }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"auto_renew" | translate}}</span>
                            <span class="info-value">{{ subscription.auto_renew ? ('yes' | translate) : ('no' | translate) }}</span>
                        </div>
                        <div class="info-item" *ngIf="subscription.is_free_plan">
                            <span class="info-label">{{"free_plan" | translate}}</span>
                            <span class="info-value">
                                <span class="status-badge status-active">{{"yes" | translate}}</span>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Dates Section -->
                <div class="section" [class.cancelled-section]="subscription.status === 'cancelled'">
                    <h3 class="section__title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        {{"subscription_dates" | translate}}
                    </h3>
                    
                    <!-- Cancellation Notice -->
                    <div *ngIf="subscription.status === 'cancelled'" class="cancellation-notice mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        <strong>{{"trial_cancelled" | translate}}</strong>
                        <p class="text-sm">{{"trial_cancelled_on" | translate}} {{ formatDate(subscription.updated_at) }}</p>
                    </div>
                    
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="info-label">{{"start_date" | translate}}</span>
                            <span class="info-value">{{ subscription.status === 'cancelled' ? formatDate(subscription.updated_at) : formatDate(subscription.start_date) }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"end_date" | translate}}</span>
                            <span class="info-value">{{ subscription.status === 'cancelled' ? formatDate(subscription.updated_at) : formatDate(subscription.end_date) }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"days_remaining" | translate}}</span>
                            <span class="info-value" [class.text-danger]="getDaysRemaining() <= 7 && getDaysRemaining() > 0" [class.text-success]="getDaysRemaining() > 7" [class.text-muted]="getDaysRemaining() === 0">
                                {{ getDaysRemaining() }} {{"days" | translate}}
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"created_at" | translate}}</span>
                            <span class="info-value">{{ formatDate(subscription.created_at) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Payment Section -->
                <div class="section">
                    <h3 class="section__title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="1" x2="12" y2="23"></line>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                        {{"payment_information" | translate}}
                    </h3>
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="info-label">{{"payment_status" | translate}}</span>
                            <span class="info-value">
                                <span class="status-badge" [ngClass]="getPaymentStatusClass(subscription.payment_status)">
                                    {{ subscription.payment_status | titlecase }}
                                </span>
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"payment_method" | translate}}</span>
                            <span class="info-value">{{ subscription.payment_method | titlecase }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"payment_amount" | translate}}</span>
                            <span class="info-value">{{ subscription.payment_amount || 0 | currencyConvert: (subscription.currency || 'BHD') }}</span>
                        </div>
                    </div>
                </div>

                <!-- AI Tokens Section -->
                <div class="section" *ngIf="subscription.ai_tokens_total > 0">
                    <h3 class="section__title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        {{"ai_tokens" | translate}}
                    </h3>
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="info-label">{{"tokens_remaining" | translate}}</span>
                            <span class="info-value info-value--large">{{ subscription.ai_tokens_remaining || 0 }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">{{"tokens_total" | translate}}</span>
                            <span class="info-value">{{ subscription.ai_tokens_total || 0 }}</span>
                        </div>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-bar__fill" 
                             [style.width.%]="getTokenUsagePercentage()"
                             [class.progress-warning]="getTokenUsagePercentage() >= 75 && getTokenUsagePercentage() < 90"
                             [class.progress-danger]="getTokenUsagePercentage() >= 90"></div>
                    </div>
                    <p class="progress-text">{{ getTokenUsagePercentage().toFixed(1) }}% {{"used" | translate}}</p>
                </div>

                <!-- Feature Usage Section -->
                <div class="section" *ngIf="subscriptionPlan && getAllSubscriptionFeatures()?.length > 0">
                    <h3 class="section__title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="9 11 12 14 22 4"></polyline>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                        </svg>
                        {{"feature_usage" | translate}}
                        <button class="btn-refresh" (click)="manualRefresh()" [disabled]="isRefreshing" title="{{'refresh' | translate}}">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" [class.spinning]="isRefreshing">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <polyline points="1 20 1 14 7 14"></polyline>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                            </svg>
                        </button>
                    </h3>
                    <div class="feature-grid">
                        <div class="feature-item" *ngFor="let feature of getAllSubscriptionFeatures()">
                            <div class="feature-name">{{ getFeatureName(feature) }}</div>
                            <div class="feature-stats">
                                <span>{{"used" | translate}}: <strong>{{ feature.used || 0 }}</strong></span>
                                <span *ngIf="feature.limit !== null && feature.limit !== -1">{{"limit" | translate}}: <strong>{{ feature.limit }}</strong></span>
                                <span *ngIf="feature.limit === null || feature.limit === -1" class="text-success">{{"unlimited" | translate}}</span>
                            </div>
                            <div class="progress-bar" *ngIf="feature.limit !== null && feature.limit !== -1 && feature.limit > 0">
                                <div class="progress-bar__fill" 
                                     [style.width.%]="getFeatureUsagePercentage(feature)"
                                     [class.progress-warning]="getFeatureUsagePercentage(feature) >= 75 && getFeatureUsagePercentage(feature) < 90"
                                     [class.progress-danger]="getFeatureUsagePercentage(feature) >= 90"></div>
                            </div>
                            <p class="progress-text" *ngIf="feature.limit !== null && feature.limit !== -1 && feature.limit > 0">
                                {{ getFeatureUsagePercentage(feature).toFixed(1) }}% {{"used" | translate}}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Sub-Feature Usage Section -->
                <div class="section" *ngIf="subscriptionPlan && getAllSubscriptionSubFeatures()?.length > 0">
                    <h3 class="section__title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                            <path d="M9 12l2 2 4-4"></path>
                        </svg>
                        {{"sub_feature_usage" | translate}}
                    </h3>
                    <div class="feature-grid">
                        <div class="feature-item feature-item--sub" *ngFor="let subFeature of getAllSubscriptionSubFeatures()">
                            <div class="feature-parent" *ngIf="getParentFeatureName(subFeature)">
                                <span class="parent-label">{{"parent_feature" | translate}}:</span>
                                <span class="parent-name">{{ getParentFeatureName(subFeature) }}</span>
                            </div>
                            <div class="feature-name">{{ getSubFeatureName(subFeature) }}</div>
                            <div class="feature-stats">
                                <span>{{"used" | translate}}: <strong>{{ subFeature.used || 0 }}</strong></span>
                                <span *ngIf="subFeature.limit !== null && subFeature.limit !== -1">{{"limit" | translate}}: <strong>{{ subFeature.limit }}</strong></span>
                                <span *ngIf="subFeature.limit === null || subFeature.limit === -1" class="text-success">{{"unlimited" | translate}}</span>
                            </div>
                            <div class="progress-bar" *ngIf="subFeature.limit !== null && subFeature.limit !== -1 && subFeature.limit > 0">
                                <div class="progress-bar__fill" 
                                     [style.width.%]="getSubFeatureUsagePercentage(subFeature)"
                                     [class.progress-warning]="getSubFeatureUsagePercentage(subFeature) >= 75 && getSubFeatureUsagePercentage(subFeature) < 90"
                                     [class.progress-danger]="getSubFeatureUsagePercentage(subFeature) >= 90"></div>
                            </div>
                            <p class="progress-text" *ngIf="subFeature.limit !== null && subFeature.limit !== -1 && subFeature.limit > 0">
                                {{ getSubFeatureUsagePercentage(subFeature).toFixed(1) }}% {{"used" | translate}}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Actions Section - Cancel Buttons -->
                <!-- Show actions if: NOT a free/default plan, OR currently in trial period -->
                <div class="actions" *ngIf="!isFreeOrDefaultPlan() || isInTrialPeriod()">
                    <!-- Trial Notice - Show only during trial period -->
                    <div *ngIf="isInTrialPeriod()" class="trial-notice">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <div>
                            <strong>{{"free_trial_active" | translate}}</strong>
                            <p>{{"trial_ends_in" | translate}} {{ getTrialDaysRemaining() }} {{"days" | translate}}</p>
                            <p class="text-sm">{{"trial_will_charge" | translate}} {{ formatDate(subscription.processerValues?.trialEndDate) }}</p>
                        </div>
                    </div>
                    
                    <!-- Scheduled Cancellation Notice - Show only for active subscriptions -->
                    <div *ngIf="subscription.status === 'active' && subscription.cancel_at_period_end" class="cancellation-notice">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <div>
                            <strong>{{"cancellation_scheduled" | translate}}</strong>
                            <p>{{"subscription_will_end_on" | translate}} {{ formatDate(subscription.end_date) }}</p>
                        </div>
                    </div>
                    
                    <!-- Cancel Free Trial Button - Show only during trial -->
                    <button class="btn btn-warning" 
                            *ngIf="isInTrialPeriod()"
                            (click)="showCancelTrialDialog()" 
                            [disabled]="isCancellingTrial">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        {{"cancel_free_trial" | translate}}
                    </button>
                    
                    <!-- Cancel Subscription Button - Show only for active subscriptions not in trial -->
                    <button class="btn btn-danger" 
                            *ngIf="subscription.status === 'active' && !subscription.cancel_at_period_end && !isInTrialPeriod()"
                            (click)="showCancelDialog()" 
                            [disabled]="isCancelling">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        {{"cancel_subscription" | translate}}
                    </button>
                </div>
            </div>

            <!-- Plan Selection Section (for all user types) -->
            <div *ngIf="(isCustomer || isVendor || isSupplier) && !isLoading" class="customer-plans-section mt-4">
                <div class="section">
                    <!-- Tabs -->
                    <div class="tabs">
                        <button 
                            class="tab-btn" 
                            [class.active]="activeTab === 'plans'"
                            (click)="setActiveTab('plans')">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                            {{"available_plans" | translate}}
                        </button>
                        <button 
                            class="tab-btn" 
                            [class.active]="activeTab === 'history'"
                            (click)="setActiveTab('history')">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                            </svg>
                            {{"purchase_history" | translate}}
                        </button>
                    </div>

                    <!-- Plans Tab Content -->
                    <div class="tab-content" *ngIf="activeTab === 'plans'">
                        <!-- Loading Plans -->
                        <div *ngIf="isLoadingPlans" class="text-center py-4">
                            <div class="spinner"></div>
                            <p>{{"loading_plans" | translate}}...</p>
                        </div>

                        <!-- Plans Grid -->
                        <div *ngIf="!isLoadingPlans && customerPlans.length > 0" class="plans-grid">
                        <div *ngFor="let plan of customerPlans; let i = index" 
                             class="plan-card"
                             [class.selected]="isPlanSelected(plan)"
                             (click)="selectPlan(plan)">
                            
                            <!-- Selected Badge -->
                            <div *ngIf="isPlanSelected(plan)" class="selected-badge">
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                </svg>
                                {{"selected" | translate}}
                            </div>

                            <!-- Plan Header -->
                            <div class="plan-header">
                                <h4 class="plan-name">{{ localize(plan.name) }}</h4>
                            </div>

                            <!-- Price Section -->
                            <div class="plan-price-section">
                                <span class="price-value">{{ calculateFinalPrice(getDisplayPrice(plan), plan) | currencyConvert: localize(plan.currency) }}</span>
                                <div class="price-period">
                                    / {{ getCurrentBillingType(plan) === 'monthly' ? ('month' | translate) : ('year' | translate) }}
                                </div>
                                <span *ngIf="hasActiveDiscount(plan)" class="price-original">
                                    {{ getDisplayPrice(plan) | currencyConvert: localize(plan.currency) }}
                                </span>
                                <span *ngIf="hasActiveDiscount(plan)" class="discount-badge">
                                    -{{"save" | translate}} {{ getCurrentDiscountRate(plan) }}%
                                </span>
                            </div>

                            <!-- Billing Type Toggle -->
                            <div class="billing-toggle" *ngIf="plan.monthly_price && plan.yearly_price">
                                <button type="button"
                                        [class.active]="getCurrentBillingType(plan) === 'monthly'"
                                        (click)="setBillingType(plan._id, 'monthly'); $event.stopPropagation()">
                                    {{"monthly" | translate}}
                                </button>
                                <button type="button"
                                        [class.active]="getCurrentBillingType(plan) === 'yearly'"
                                        (click)="setBillingType(plan._id, 'yearly'); $event.stopPropagation()">
                                    {{"yearly" | translate}}
                                </button>
                            </div>

                            <!-- Plan Features -->
                            <div class="plan-features">
                                <div class="feature-item" *ngIf="plan.ai_token_limit > 0">
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                    </svg>
                                    <span>{{ plan.ai_token_limit | number }} {{"ai_tokens" | translate}}</span>
                                </div>
                                <div class="feature-item" *ngIf="isTrialEligible && plan.trial_days > 0">
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                    </svg>
                                    <span>{{ plan.trial_days }} {{"days_free_trial" | translate}}</span>
                                </div>

                                <!-- Expandable Features -->
                                <div *ngIf="plan.subscription_features_populated?.length > 0" class="mt-2">
                                    <div class="flex items-start gap-2 cursor-pointer" (click)="toggleFeatures(plan._id); $event.stopPropagation()">
                                        <svg class="w-4 h-4 flex-shrink-0 mt-0.5 transition-transform duration-200 text-emerald-500"
                                             [class.rotate-90]="expandedFeatures[plan._id]"
                                             fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                                        </svg>
                                        <span class="text-xs font-medium leading-tight flex items-center gap-1 text-gray-600">
                                            {{ plan.subscription_features_populated.length }} {{"additional_features" | translate}}
                                                <svg class="w-3 h-3 transition-transform duration-200 text-gray-500"
                                                 [class.rotate-180]="expandedFeatures[plan._id]"
                                                 fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                                            </svg>
                                        </span>
                                    </div>

                                    <div *ngIf="expandedFeatures[plan._id]" class="mt-2 pl-6 space-y-1.5 animate-slideDown">
                                        <ng-container *ngFor="let feature of plan.subscription_features_populated">
                                            <div class="flex items-start gap-1.5">
                                                <svg class="w-3 h-3 flex-shrink-0 mt-0.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                                                </svg>
                                                <span class="text-xs leading-tight flex-1 text-gray-700">{{ localize(feature.name) }}</span>
                                                <span class="text-[10px] font-semibold opacity-90 whitespace-nowrap text-gray-600">
                                                    {{ getUsageLimitText(getFeatureUsageLimit(plan, feature._id)) }}
                                                </span>
                                            </div>

                                            <div *ngIf="getPlanSubFeaturesForFeature(plan, feature._id).length > 0" class="pl-5 mt-1 space-y-1">
                                                <div *ngFor="let sf of getPlanSubFeaturesForFeature(plan, feature._id)" class="flex items-start gap-1.5">
                                                    <svg class="w-3 h-3 flex-shrink-0 mt-0.5 opacity-90 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                                                    </svg>
                                                    <span class="text-xs leading-tight flex-1 text-gray-700">
                                                        {{ localize(sf?.name) || sf?.subFeatureCode || '(sub-feature)' }}
                                                    </span>
                                                    <span class="text-[10px] font-semibold opacity-90 whitespace-nowrap text-gray-600">
                                                        {{ getUsageLimitText(getSubFeatureUsageLimit(plan, sf._id)) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </ng-container>
                                    </div>
                                </div>
                            </div>

                            <!-- Select Button -->
                            <button class="select-btn" [class.selected]="isPlanSelected(plan)">
                                <span *ngIf="!isPlanSelected(plan)">{{"click_to_select" | translate}}</span>
                                <span *ngIf="isPlanSelected(plan)">{{"selected" | translate}} ✓</span>
                            </button>
                        </div>
                        </div>

                        <!-- No Plans Available -->
                        <div *ngIf="!isLoadingPlans && customerPlans.length === 0" class="no-plans">
                            <p>{{"no_plans_available_for_your_country" | translate}}</p>
                        </div>

                        <!-- Subscribe Button -->
                        <div *ngIf="selectedPlan" class="subscribe-action">
                            <button 
                                class="btn btn-primary" 
                                (click)="subscribeToPlan()" 
                                [disabled]="isSubscribing || !canSubscribeToNewPlan()">
                                <span *ngIf="isSubscribing" class="spinner-small"></span>
                                {{"subscribe_now" | translate}}
                            </button>
                            <p *ngIf="!canSubscribeToNewPlan()" class="text-sm text-red-500 mt-2">
                                {{"cannot_change_plan_message" | translate}}
                            </p>
                        </div>
                    </div>

                    <!-- History Tab Content -->
                    <div class="tab-content" *ngIf="activeTab === 'history'">
                        <!-- Loading History -->
                        <div *ngIf="isLoadingHistory" class="text-center py-4">
                            <div class="spinner"></div>
                            <p>{{"loading" | translate}}...</p>
                        </div>

                        <!-- History Table -->
                        <div *ngIf="!isLoadingHistory && purchaseHistory.length > 0" class="history-table">
                            <div class="table-header">
                                <div class="col">{{"plan" | translate}}</div>
                                <div class="col">{{"billing_type" | translate}}</div>
                                <div class="col">{{"price_paid" | translate}}</div>
                                <div class="col">{{"ai_tokens" | translate}}</div>
                                <div class="col">{{"date" | translate}}</div>
                                <div class="col">{{"status" | translate}}</div>
                            </div>
                            <div class="table-row" *ngFor="let purchase of purchaseHistory">
                                <div class="col plan-name">{{ getPurchasePlanName(purchase) }}</div>
                                <div class="col">
                                    <span *ngIf="purchase.billingType === 'monthly'">{{"monthly" | translate}}</span>
                                    <span *ngIf="purchase.billingType === 'annualy' || purchase.billingType === 'yearly'">{{"yearly" | translate}}</span>
                                </div>
                                <div class="col price">{{ purchase.pricePaid | currencyConvert: (purchase.currency || 'BHD') }}</div>
                                <div class="col tokens" *ngIf="purchase.aiTokensIncluded > 0">+{{ formatNumber(purchase.aiTokensIncluded) }}</div>
                                <div class="col tokens" *ngIf="!purchase.aiTokensIncluded || purchase.aiTokensIncluded === 0">-</div>
                                <div class="col date">{{ formatPurchaseDate(purchase.purchasedAt) }}</div>
                                <div class="col">
                                    <span class="status-badge" [ngClass]="getStatusClass(purchase.status || 'completed')">
                                        {{ purchase.status || 'completed' | translate }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- No History -->
                        <div *ngIf="!isLoadingHistory && purchaseHistory.length === 0" class="no-data">
                            <div class="no-data__icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                </svg>
                            </div>
                            <p>{{"no_purchase_history" | translate}}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <div class="modal-overlay" *ngIf="showCancelConfirm" (click)="hideCancelDialog()">
        <div class="modal-content" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>{{"cancel_subscription" | translate}}</h3>
            </div>
            <div class="modal-body">
                <p>{{"cancel_subscription_confirmation_scheduled" | translate}}</p>
                <p class="text-sm text-gray-600 mt-2">
                    {{"subscription_active_until" | translate}} {{ formatDate(subscription?.end_date) }}
                </p>
            </div>
            <div class="modal-footer">
                <button class="btn btn-secondary" (click)="hideCancelDialog()" [disabled]="isCancelling">
                    {{"no_keep" | translate}}
                </button>
                <button class="btn btn-danger" (click)="confirmCancel()" [disabled]="isCancelling">
                    <span *ngIf="isCancelling" class="spinner-small"></span>
                    {{"yes_cancel" | translate}}
                </button>
            </div>
        </div>
    </div>
    
    <!-- Cancel Free Trial Confirmation Modal -->
    <div class="modal-overlay" *ngIf="showCancelTrialConfirm" (click)="hideCancelTrialDialog()">
        <div class="modal-content" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>{{"cancel_trial_confirmation" | translate}}</h3>
            </div>
            <div class="modal-body">
                <p class="trial-cancel-notice">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <strong>{{"important" | translate}}:</strong> {{"trial_cancel_notice" | translate}}
                </p>
                <p class="mt-2">{{"trial_cancel_no_charge" | translate}}</p>
            </div>
            <div class="modal-footer">
                <button class="btn btn-secondary" (click)="hideCancelTrialDialog()" [disabled]="isCancellingTrial">
                    {{"no_keep_trial" | translate}}
                </button>
                <button class="btn btn-danger" (click)="confirmCancelTrial()" [disabled]="isCancellingTrial">
                    <span *ngIf="isCancellingTrial" class="spinner-small"></span>
                    {{"yes_cancel_trial" | translate}}
                </button>
            </div>
        </div>
    </div>
</div>
